import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../useTheme";

const API_URL = "https://2504.ict-expert.ch/api";

function Home() {
  const [taches, setTaches] = useState([]);
  const [sortField, setSortField] = useState("date");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [selectedEtat, setSelectedEtat] = useState("all");
  const { theme, toggleTheme } = useTheme();
  const [sortDirection, setSortDirection] = useState("asc");

  const [form, setForm] = useState({
    titre: "",
    description: "",
    date_limite: "",
    etat: "en cours",
    categories: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userEmail = localStorage.getItem("userEmail");

  /* ========= API CALLS ========= */

  async function fetchTaches() {
    try {
      const res = await fetch(`${API_URL}/taches_list.php`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("taches:", data);

      if (res.status === 401) {
        // token plus valide → on déconnecte
        localStorage.removeItem("token");
        localStorage.removeItem("userEmail");
        navigate("/login", { replace: true });
        return;
      }

      if (Array.isArray(data)) {
        setTaches(data);
      } else {
        console.error("taches error:", data);
        setTaches([]);
      }
    } catch (err) {
      console.error(err);
      setTaches([]);
    }
  }

  async function fetchCategories() {
    try {
      const res = await fetch(`${API_URL}/taches_categorie.php`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      console.log("categories:", data);

      if (res.status === 401) {
        // token plus valable → on déconnecte proprement
        localStorage.removeItem("token");
        localStorage.removeItem("userEmail");
        navigate("/login", { replace: true });
        return;
      }

      // on s’assure que c’est bien un tableau
      if (Array.isArray(data)) {
        setCategories(data);
      } else {
        console.error("categories error:", data);
        setCategories([]);
      }
    } catch (err) {
      console.error(err);
      setCategories([]);
    }
  }

  async function createTache(tache) {
    const res = await fetch(`${API_URL}/taches_create.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(tache),
    });

    const data = await res.json();
    console.log("create:", data);
    await fetchTaches();
    await fetchCategories();
  }

  async function updateTache(tache) {
    const res = await fetch(`${API_URL}/taches_update.php`, {
      method: "PUT", // adapte à POST si besoin
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(tache),
    });

    const data = await res.json();
    console.log("update:", data);
    await fetchTaches();
    await fetchCategories();
  }

  async function deleteTache(id) {
    const res = await fetch(`${API_URL}/taches_delete.php?id=` + id, {
      method: "DELETE", // adapte à POST si besoin
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();
    console.log("delete:", data);
    await fetchTaches();
  }

  async function handleLogout() {
    try {
      if (token) {
        await fetch(`${API_URL}/logout.php`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");
      navigate("/login", { replace: true });
    }
  }

  /* ========= LIFECYCLE ========= */

  useEffect(() => {
    fetchTaches();
    fetchCategories();
  }, []);

  /* ========= FORM LOGIC ========= */

  function resetForm() {
    setForm({
      titre: "",
      description: "",
      date_limite: "",
      etat: "en cours",
      categories: "",
    });
    setIsEditing(false);
    setEditingId(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleCategoriesChange(e) {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, categories: value }));

    // on récupère le dernier "morceau" (après la dernière virgule)
    const parts = value.split(",");
    const lastPartRaw = parts[parts.length - 1] || "";
    const lastPart = lastPartRaw.trim().toLowerCase();

    if (!lastPart) {
      setCategorySuggestions([]);
      return;
    }

    // on évite de proposer des catégories déjà tapées
    const existingParts = parts
      .slice(0, -1)
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);

    const suggestions = categoryNames.filter((name) => {
      const lower = name.toLowerCase();
      return lower.startsWith(lastPart) && !existingParts.includes(lower);
    });

    setCategorySuggestions(suggestions);
    setShowCategorySuggestions(suggestions.length > 0);
  }

  function handleCategorySuggestionClick(name) {
    const current = form.categories || "";
    const parts = current.split(",");

    // on remplace juste le dernier morceau par la suggestion clean
    parts[parts.length - 1] = " " + name;
    const newValue = parts.join(",").replace(/^,\s*/, "");

    setForm((prev) => ({ ...prev, categories: newValue }));
    setCategorySuggestions([]);
    setShowCategorySuggestions(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titre.trim()) return;

    const payload = {
      titre: form.titre,
      description: form.description || null,
      date_limite: form.date_limite || null,
      etat: form.etat,
      categories: form.categories || "",
    };

    try {
      if (isEditing && editingId) {
        await updateTache({ id_tache: editingId, ...payload });
      } else {
        await createTache(payload);
      }
      resetForm();
    } catch (err) {
      console.error(err);
    }
  }

  function handleEditClick(tache) {
    setForm({
      titre: tache.titre || "",
      description: tache.description || "",
      date_limite: tache.date_limite || "",
      etat: tache.etat || "en cours",
      categories: tache.categories || "",
    });
    setIsEditing(true);
    setEditingId(tache.id_tache);
  }

  async function handleDeleteClick(id) {
    if (!window.confirm("Supprimer cette tâche ?")) return;

    await deleteTache(id);

    // Si on vient de supprimer la tâche en cours d'édition,
    // on remet le panneau de droite à zéro
    if (editingId === id) {
      resetForm();
    }
  }

  async function handleDeleteCategory(cat) {
    if (
      !window.confirm(
        `Supprimer définitivement la catégorie "${cat.nom}" ? (Elle sera retirée de toutes les tâches)`
      )
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/categorie_delete.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id_cate: cat.id_cate }),
      });

      const data = await res.json();
      console.log("delete category:", data);

      // si on était en train de filtrer sur cette catégorie, on revient sur "Toutes"
      if (selectedCategory === cat.nom) {
        setSelectedCategory("all");
      }

      // on recharge catégories + tâches
      await fetchCategories();
      await fetchTaches();
    } catch (err) {
      console.error(err);
    }
  }

  async function handleRenameCategory(cat) {
    const newName = window.prompt(
      `Nouveau nom pour la catégorie "${cat.nom}" :`,
      cat.nom
    );

    if (newName === null) return;

    const trimmed = newName.trim();
    if (!trimmed || trimmed === cat.nom) return;

    try {
      const res = await fetch(`${API_URL}/categorie_update.php`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id_cate: cat.id_cate, nom: trimmed }),
      });

      const data = await res.json();
      console.log("rename category:", data);

      if (!res.ok || data.success !== true) {
        console.error("Erreur renommage catégorie", data);
        return;
      }

      if (selectedCategory === cat.nom) {
        setSelectedCategory(trimmed);
      }

      await fetchCategories();
      await fetchTaches();
    } catch (err) {
      console.error(err);
    }
  }

  /* ========= CATEGORIES ========= */

  const allCategories = categories;
  const categoryNames = allCategories.map((c) => c.nom).filter(Boolean);

  /* ========= TRI + FILTRE LOGIC (NOUVEAU) ========= */

  function toggleSortDirection(field) {
    // Si on change de champ de tri, on reset la direction à 'asc'
    if (sortField !== field) {
      setSortField(field);
      setSortDirection("asc");
    } else {
      // Sinon, on inverse la direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    }
  }

  /* ========= TRI + FILTRE ========= */

  const filteredTaches = taches.filter((t) => {
    let matchCategory = true;
    if (selectedCategory !== "all") {
      if (!t.categories) {
        matchCategory = false;
      } else {
        const cats = t.categories.split(",").map((c) => c.trim());
        matchCategory = cats.includes(selectedCategory);
      }
    }

    // --- filtre état ---
    let matchEtat = true;
    if (selectedEtat !== "all") {
      // t.etat en DB = "en cours" ou "terminée"
      matchEtat = (t.etat || "").toLowerCase() === selectedEtat.toLowerCase();
    }

    return matchCategory && matchEtat;
  });

  const sortedTaches = [...filteredTaches].sort((a, b) => {
    const dir = sortDirection === "asc" ? 1 : -1;
    let comparison = 0;

    if (sortField === "date") {
      const da = a.date_limite ? new Date(a.date_limite) : null;
      const db = b.date_limite ? new Date(b.date_limite) : null;

      // Logique de tri par date (les null vont à la fin en 'asc', au début en 'desc')
      if (!da && !db) comparison = 0;
      else if (!da) comparison = 1;
      else if (!db) comparison = -1;
      else comparison = da.getTime() - db.getTime();
    } else if (sortField === "titre") {
      // Tri par titre
      comparison = (a.titre || "").localeCompare(b.titre || "");
    }
    // Rétablit le comportement par défaut si sortField est invalide (non nécessaire ici)
    else {
      comparison = (a.titre || "").localeCompare(b.titre || "");
    }

    return comparison * dir;
  });
  /* ========= RENDER ========= */

  return (
    <div className="app-root">
      <div className="layout">
        {/* === SIDEBAR === */}
        <aside className="sidebar">
          <div className="sidebar-header">
            <span className="logo-dot" />
            <h2>Menu</h2>
          </div>

          <div className="sidebar-section">
            <p className="sidebar-section-title">Tâches</p>
            <button className="sidebar-item sidebar-item-active">
              Aujourd&apos;hui
            </button>
          </div>

          <div className="sidebar-section">
            <p className="sidebar-section-title">États</p>

            <button
              className={
                "sidebar-pill " +
                (selectedEtat === "all" ? "sidebar-pill-active" : "")
              }
              onClick={() => setSelectedEtat("all")}
            >
              Tous
            </button>

            <button
              className={
                "sidebar-pill " +
                (selectedEtat === "en cours" ? "sidebar-pill-active" : "")
              }
              onClick={() => setSelectedEtat("en cours")}
            >
              En cours
            </button>

            <button
              className={
                "sidebar-pill " +
                (selectedEtat === "terminée" ? "sidebar-pill-active" : "")
              }
              onClick={() => setSelectedEtat("terminée")}
            >
              Terminées
            </button>
          </div>

          <div className="sidebar-section">
            <p className="sidebar-section-title">Catégories</p>

            <button
              className={
                "sidebar-pill " +
                (selectedCategory === "all" ? "sidebar-pill-active" : "")
              }
              onClick={() => setSelectedCategory("all")}
            >
              Toutes
            </button>

            {allCategories.map((cat) => (
              <button
                key={cat.id_cate}
                className={
                  "sidebar-pill sidebar-pill-with-icons " +
                  (selectedCategory === cat.nom ? "sidebar-pill-active" : "")
                }
                onClick={() => setSelectedCategory(cat.nom)}
              >
                <span className="sidebar-pill-label">{cat.nom}</span>

                <span className="sidebar-pill-icons">
                  <span
                    className="sidebar-pill-edit"
                    onClick={(e) => {
                      e.stopPropagation(); // ne change pas le filtre
                      handleRenameCategory(cat);
                    }}
                  >
                    ✏️
                  </span>
                  <span
                    className="sidebar-pill-delete"
                    onClick={(e) => {
                      e.stopPropagation(); // ne change pas le filtre
                      handleDeleteCategory(cat);
                    }}
                  >
                    ✕
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="sidebar-footer">
            {userEmail && (
              <div className="sidebar-user">Connecté : {userEmail}</div>
            )}
            <button className="sidebar-logout" onClick={handleLogout}>
              Se déconnecter
            </button>
          </div>
        </aside>

        {/* === MAIN CONTENT === */}
        <main className="main">
          <section className="tasks-column">
            <header className="tasks-header">
              <div className="tasks-header-left">
                <h1>Aujourd&apos;hui</h1>
                <span className="tasks-count">{sortedTaches.length}</span>
              </div>

              <div className="tasks-header-right">
                <button
                  className="theme-toggle-btn"
                  type="button"
                  onClick={toggleTheme}
                >
                  {theme === "light" ? "🌙" : "☀️"}
                </button>
                <button className="add-task-btn" onClick={resetForm}>
                  + Nouvelle tâche
                </button>
              </div>
            </header>

            <div className="tasks-toolbar">
              <label>
                Trier par {/* Remplacer le <select> par des boutons/texte */}
                <span
                  className="sort-field-toggle"
                  onClick={() => toggleSortDirection("date")}
                >
                  Date limite
                  {/* Affichage de l'icône de direction */}
                  {sortField === "date" && (
                    <span className="sort-direction-icon">
                      {sortDirection === "asc" ? " ▲" : " ▼"}
                    </span>
                  )}
                </span>
                {" | "}
                <span
                  className="sort-field-toggle"
                  onClick={() => toggleSortDirection("titre")}
                >
                  Titre
                  {/* Affichage de l'icône de direction */}
                  {sortField === "titre" && (
                    <span className="sort-direction-icon">
                      {sortDirection === "asc" ? " ▲" : " ▼"}
                    </span>
                  )}
                </span>
              </label>
            </div>

            <ul className="task-list">
              {sortedTaches.map((tache) => (
                <li
                  key={tache.id_tache}
                  className="task-row"
                  onClick={() => handleEditClick(tache)}
                >
                  <div className="task-row-main">
                    <div className="task-row-title">
                      <input
                        type="checkbox"
                        readOnly
                        checked={editingId === tache.id_tache} // ✅ une seule cochée
                      />
                      <span>{tache.titre}</span>
                    </div>
                    <button
                      className="task-row-arrow"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(tache);
                      }}
                    >
                      &gt;
                    </button>
                  </div>

                  <div className="task-row-meta">
                    {tache.date_limite && (
                      <span className="task-chip">📅 {tache.date_limite}</span>
                    )}
                    {tache.etat && (
                      <span className="task-chip">{tache.etat}</span>
                    )}
                    {tache.categories && (
                      <span className="task-chip task-chip-category">
                        {tache.categories}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Colonne droite : détails / formulaire */}
          <section className="detail-column">
            <header className="detail-header">
              <h2>Tâche</h2>
            </header>

            <form className="detail-form" onSubmit={handleSubmit}>
              <label className="detail-field">
                <span>Titre</span>
                <input
                  type="text"
                  name="titre"
                  value={form.titre}
                  onChange={handleChange}
                  placeholder="Titre de la tâche"
                  required
                />
              </label>

              <label className="detail-field">
                <span>Description</span>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Description"
                  rows={4}
                />
              </label>

              <div className="detail-row">
                <label className="detail-field">
                  <span>État</span>
                  <select name="etat" value={form.etat} onChange={handleChange}>
                    <option value="en cours">En cours</option>
                    <option value="terminée">Terminée</option>
                  </select>
                </label>

                <label className="detail-field">
                  <span>Date limite</span>
                  <input
                    type="date"
                    name="date_limite"
                    value={form.date_limite}
                    onChange={handleChange}
                  />
                </label>
              </div>

              {/*  Nouveau champ catégories ici */}
              <label className="detail-field">
                <span>Catégories</span>
                <div className="category-input-wrapper">
                  <input
                    type="text"
                    name="categories"
                    value={form.categories}
                    onChange={handleCategoriesChange}
                    onFocus={() => {
                      if (form.categories && form.categories.trim() !== "") {
                        setShowCategorySuggestions(
                          categorySuggestions.length > 0
                        );
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowCategorySuggestions(false), 150);
                    }}
                    placeholder="Ex : Perso, École"
                    autoComplete="off"
                  />

                  <span className="category-info-text">
                    Choisir / Créer une catégorie
                  </span>

                  {showCategorySuggestions &&
                    categorySuggestions.length > 0 && (
                      <ul className="category-suggestions">
                        {categorySuggestions.map((name) => (
                          <li
                            key={name}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleCategorySuggestionClick(name);
                            }}
                          >
                            {name}
                          </li>
                        ))}
                      </ul>
                    )}
                </div>
              </label>

              <div className="detail-actions">
                {isEditing && (
                  <button
                    type="button"
                    className="danger-btn"
                    onClick={() => handleDeleteClick(editingId)}
                  >
                    Supprimer
                  </button>
                )}
                <button type="submit" className="primary-btn">
                  {isEditing ? "Enregistrer" : "Créer la tâche"}
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
}

export default Home;
